import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given cookie consent
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    if (!cookieConsent) {
      // Wait a moment before showing to not interfere with age verification if it exists
      setTimeout(() => {
        setShowPopup(true);
      }, 1500);
    } else {
      // Load saved preferences if consent already given
      const savedPrefs = localStorage.getItem('cookiePreferences');
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowPopup(false);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowPopup(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyEssential));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowPopup(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Essential cookies can't be disabled
    setPreferences({
      ...preferences,
      [key]: !preferences[key],
    });
  };

  if (!showPopup) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40 max-w-sm md:max-w-md">
      {!showSettings ? (
        <div className="glass rounded-xl p-4 md:p-5 shadow-2xl border border-white/20 animate-slide-up">
          <div className="flex items-start space-x-3 mb-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <Cookie className="text-white" size={20} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-bold text-white mb-1">
                Cookie Preferences
              </h3>
              <p className="text-xs md:text-sm text-white/80 leading-relaxed mb-3">
                We use cookies to enhance your browsing experience. You can customize your preferences.
              </p>
            </div>
            <button
              onClick={() => setShowPopup(false)}
              className="flex-shrink-0 text-white/60 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleRejectAll}
              className="px-3 py-1.5 rounded-lg text-white/80 font-semibold hover:text-white transition-colors text-xs"
            >
              Reject
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center justify-center space-x-1 px-3 py-1.5 rounded-lg glass border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors text-xs"
            >
              <Settings size={14} />
              <span>Customize</span>
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 text-xs"
            >
              Accept All
            </button>
          </div>

          <p className="text-xs text-white/50 mt-3">
            Learn more in our{' '}
            <Link to="/cookie-policy" className="text-yellow-400 hover:text-yellow-300 underline">
              Cookie Policy
            </Link>
          </p>
        </div>
      ) : (
        <div className="glass rounded-xl p-4 md:p-5 shadow-2xl border border-white/20 animate-slide-up">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base md:text-lg font-bold text-white">Cookie Settings</h3>
            <button
              onClick={() => setShowSettings(false)}
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between p-2.5 glass rounded-lg">
              <div>
                <p className="text-white font-semibold text-xs md:text-sm">Essential Cookies</p>
                <p className="text-white/60 text-xs">Required for website function</p>
              </div>
              <div className="w-10 h-5 bg-yellow-400 rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-50">
                <div className="w-3.5 h-3.5 bg-white rounded-full"></div>
              </div>
            </div>

            <div className="flex items-center justify-between p-2.5 glass rounded-lg">
              <div>
                <p className="text-white font-semibold text-xs md:text-sm">Analytics Cookies</p>
                <p className="text-white/60 text-xs">Help us improve website</p>
              </div>
              <button
                onClick={() => togglePreference('analytics')}
                className={`w-10 h-5 rounded-full flex items-center transition-colors duration-300 ${
                  preferences.analytics
                    ? 'bg-yellow-400 justify-end'
                    : 'bg-white/20 justify-start'
                } px-1`}
              >
                <div className="w-3.5 h-3.5 bg-white rounded-full"></div>
              </button>
            </div>

            <div className="flex items-center justify-between p-2.5 glass rounded-lg">
              <div>
                <p className="text-white font-semibold text-xs md:text-sm">Marketing Cookies</p>
                <p className="text-white/60 text-xs">Personalized advertising</p>
              </div>
              <button
                onClick={() => togglePreference('marketing')}
                className={`w-10 h-5 rounded-full flex items-center transition-colors duration-300 ${
                  preferences.marketing
                    ? 'bg-yellow-400 justify-end'
                    : 'bg-white/20 justify-start'
                } px-1`}
              >
                <div className="w-3.5 h-3.5 bg-white rounded-full"></div>
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleRejectAll}
              className="flex-1 px-3 py-1.5 rounded-lg text-white/80 font-semibold hover:text-white transition-colors text-xs"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptSelected}
              className="flex-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 text-xs"
            >
              Save
            </button>
          </div>

          <p className="text-xs text-white/50 mt-3">
            Learn more in our{' '}
            <Link to="/cookie-policy" className="text-yellow-400 hover:text-yellow-300 underline">
              Cookie Policy
            </Link>
          </p>
        </div>
      )}
    </div>
  );
};

export default CookieConsent;

